import { policyRef } from '@ias/shared';

import { cn } from '@/lib/utils';

/**
 * How a policy is named everywhere it is listed: the plate first, the policy number in
 * brackets behind it.
 *
 * The order is how the office talks. Nobody says «818861529 λήγει σε 15 μέρες»; they say
 * «το ΥΗΚ3984 λήγει» and look the number up afterwards to type it. So the plate gets the
 * ink and never shrinks, and the number goes muted and truncates behind it — on a narrow
 * column the half worth losing is the half nobody reads aloud.
 *
 * Health, property and life policies insure no vehicle: `plates` is empty, the number
 * takes the whole line, and no empty bracket appears. More than one car shows the first
 * plate and a count, because the row has to stay one row.
 *
 * Mirrors `PolicyLabel` on the server, which formats the same thing for notifications and
 * search hits — the two must agree, or one renewal reads two ways on two screens.
 */
export function PolicyRef({
  policyNumber,
  plates,
  className,
  /** Drops the plate's emphasis, for places that are already all-muted metadata. */
  muted = false,
}: {
  policyNumber: string;
  plates?: readonly string[] | null;
  className?: string;
  muted?: boolean;
}) {
  const { plate, more, number } = policyRef(policyNumber, plates);

  if (!plate) return <span className={cn('tabular', className)}>{number}</span>;

  return (
    <span className={cn('tabular inline-flex min-w-0 items-baseline gap-1', className)}>
      <span className={cn('shrink-0 font-medium', !muted && 'text-foreground')}>
        {plate}
        {more > 0 && <span className="text-muted-foreground font-normal"> +{more}</span>}
      </span>
      <span className="text-muted-foreground truncate font-normal">({number})</span>
    </span>
  );
}
